import React, { useState } from 'react';
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { uploadLanguageFile } from '../../../api/admin/languageManagement';
import { refreshTranslations } from '../../../utils/translationRefresh';
import { useTranslation } from '../../../hooks/useTranslation';

const UploadTab = ({platform}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const languages = [
    { code: 'en', name: t('languageManagement').uploadTab.languages.english, key: 'english', flag: 'US' },
    { code: 'ka', name: t('languageManagement').uploadTab.languages.georgian, key: 'georgian', flag: 'GE' },
    { code: 'ru', name: t('languageManagement').uploadTab.languages.russian, key: 'russian', flag: 'RU' }
  ];

  const [selectedFiles, setSelectedFiles] = useState({
    english: null,
    georgian: null,
    russian: null
  });

  const [inputKeys, setInputKeys] = useState({
    english: Date.now(),
    georgian: Date.now() + 1,
    russian: Date.now() + 2
  });

  const [uploadingLanguages, setUploadingLanguages] = useState({
    english: false,
    georgian: false,
    russian: false
  });

  const handleFileSelect = (languageKey, event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/json') {
      toast.error("Only json files are allowed");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should not exceed 5 Mb");
      return;
    }

    setSelectedFiles(prev => ({ ...prev, [languageKey]: file }));
  };

  const handleUpload = async (languageKey, languageCode, languageName) => {
    const file = selectedFiles[languageKey];
    if (!file) {
      toast.error('Please select file');
      return;
    }

    setUploadingLanguages(prev => ({ ...prev, [languageKey]: true }));

    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', languageName);
    formData.append('languageCode', languageCode);
    formData.append('platform', platform);

    try {
      await uploadLanguageFile(formData);

      toast.success("Uploaded successfully.");

      // Reset selected file and input
      setSelectedFiles(prev => ({ ...prev, [languageKey]: null }));
      setInputKeys(prev => ({ ...prev, [languageKey]: Date.now() }));

      queryClient.invalidateQueries(["languageFiles"]);
      refreshTranslations();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || t('languageManagement').uploadTab.messages.uploadFailed;
      toast.error(errorMessage);
    } finally {
      setUploadingLanguages(prev => ({ ...prev, [languageKey]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('languageManagement').uploadTab.title}</h3>
        <p className="text-gray-600">
          {t('languageManagement').uploadTab.description}
        </p>
      </div>

      {/* Upload Guidelines */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-sm font-medium">i</span>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">{t('languageManagement').uploadTab.guidelines.title}</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {t('languageManagement').uploadTab.guidelines.rules?.map((rule, index) => (
                <li key={index}>• {rule}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Language Upload Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {languages.map(language => (
          <div key={language.key} className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <div className="text-2xl font-bold mb-1">{language.flag}</div>
            <div className="text-lg font-medium mb-2">{language.name}</div>
            <p className="text-sm text-gray-600 mb-4">{t('languageManagement.uploadTab.uploadDescription', { language: language.name })}</p>

            <input
              key={inputKeys[language.key]} // resets input
              type="file"
              accept=".json"
              onChange={e => handleFileSelect(language.key, e)}
              className="hidden"
              id={`${language.key}-file`}
            />

            <label
              htmlFor={`${language.key}-file`}
              className="inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
            >
              {t('languageManagement').uploadTab.selectFile}
            </label>

            {selectedFiles[language.key] && (
              <div className="mt-2 text-sm text-gray-600">
                <p className="font-medium">{t('languageManagement').uploadTab.fileSelected}: {selectedFiles[language.key].name}</p>
                <p>{t('languageManagement').uploadTab.fileSize}: {(selectedFiles[language.key].size / 1024).toFixed(2)} KB</p>
              </div>
            )}

            <button
              onClick={() => handleUpload(language.key, language.code, language.name)}
              disabled={!selectedFiles[language.key] || uploadingLanguages[language.key]}
              className="mt-3 w-full px-4 py-2 rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
            >
              {uploadingLanguages[language.key] ? t('languageManagement').uploadTab.uploading : t('languageManagement').uploadTab.uploadFile}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UploadTab;

